export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type UnauthorizedHandler = () => void;

export class HttpClient {
  private csrfToken: string | null = null;
  private unauthorizedHandler: UnauthorizedHandler | null = null;

  constructor(private readonly baseUrl: string = '') {}

  setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
    this.unauthorizedHandler = handler;
  }

  async refreshCsrf(): Promise<void> {
    const response = await fetch(this.baseUrl + '/api/auth/csrf', {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new ApiError(response.status, `Request failed with HTTP ${response.status}.`);
    }
    this.csrfToken = this.readCookie('XSRF-TOKEN');
    if (!this.csrfToken) {
      throw new ApiError(0, 'The security token could not be read.');
    }
  }

  clearCsrf() {
    this.csrfToken = null;
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const prefix = `${encodeURIComponent(name)}=`;
    const entry = document.cookie.split('; ').find((value) => value.startsWith(prefix));
    return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
  }

  private async parseErrorBody(res: Response): Promise<string> {
    try {
      const body = (await res.json()) as {
        error?: string;
        errors?: string[] | Record<string, string[]>;
        title?: string;
      };
      if (Array.isArray(body.errors) && body.errors.length) return body.errors.join('\n');
      if (body.errors && !Array.isArray(body.errors)) {
        const messages = Object.values(body.errors).flat();
        if (messages.length) return messages.join('\n');
      }
      if (body.error) return body.error;
      if (body.title) return body.title;
    } catch {
      // Fall through to the status message.
    }
    return `Request failed with HTTP ${res.status}.`;
  }

  private async handle<T>(res: Response): Promise<T> {
    if (!res.ok) {
      if (res.status === 401) this.unauthorizedHandler?.();
      const message = await this.parseErrorBody(res);
      throw new ApiError(res.status, message);
    }
    if (res.status === 204 || res.headers.get('Content-Length') === '0') {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    const headers = new Headers(init?.headers);
    let requestBody: BodyInit | undefined;
    if (body instanceof FormData) {
      requestBody = body;
    } else if (body !== undefined) {
      headers.set('Content-Type', 'application/json');
      requestBody = JSON.stringify(body);
    }

    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      if (!this.csrfToken) await this.refreshCsrf();
      headers.set('X-XSRF-TOKEN', this.csrfToken!);
    }

    const response = await fetch(this.baseUrl + path, {
      ...init,
      method,
      body: requestBody,
      headers,
      credentials: 'include',
    });
    return this.handle<T>(response);
  }

  get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('GET', path, undefined, init);
  }

  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('POST', path, body, init);
  }

  put<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('PUT', path, body, init);
  }

  patch<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('PATCH', path, body, init);
  }

  delete<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, init);
  }
}

export const api = new HttpClient();
